"use client";

import { createContext, useContext, useRef } from "react";
import { create, useStore } from "zustand";
import { immer } from "zustand/middleware/immer";

import { generateStackKey } from "./utils";

import type { PropsWithChildren } from "react";
import type { ParamsOf, RoutesOf, ScreensConfig, TypedNavigationActions } from "./types";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type TransitionType = "push" | "replace";

export interface StackItem<TParams = Record<string, unknown>> {
  key: string;
  name: string;
  params: TParams;
  transitionType?: TransitionType;
}

export interface StackState {
  stack: StackItem[];
  currentRoute: string;
  params: Record<string, unknown>;
  canPop: boolean;
}

const EMPTY_STACK_STATE: StackState = {
  stack: [],
  currentRoute: "",
  params: {},
  canPop: false,
};

export interface StackStoreState {
  /** Map of stackId -> stack state */
  stacks: Record<string, StackState>;

  /**
   * Initialize a stack with an initial route.
   * If the stack already exists and has items, this is a no-op.
   */
  initStack: (stackId: string, initialRoute: string, params?: Record<string, unknown>) => void;

  /** Push a new screen onto the stack */
  push: (stackId: string, name: string, params?: Record<string, unknown>) => void;

  /** Pop the top screen from the stack */
  pop: (stackId: string) => void;

  /** Replace the current screen with a new one */
  replace: (stackId: string, name: string, params?: Record<string, unknown>) => void;

  /** Reset the stack to a single screen */
  reset: (stackId: string, name: string, params?: Record<string, unknown>) => void;

  /** Pop the stack to the first screen matching the given route name, preserving keys below it */
  popTo: (stackId: string, targetName: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Creator
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a stack store that manages multiple stacks by ID.
 * Each stack is independent and identified by a unique stackId.
 *
 * @see https://docs.pmnd.rs/zustand/guides/initialize-state-with-props#store-creator-with-createstore
 */
export const createStackStore = () =>
  create<StackStoreState>()(
    immer(set => ({
      stacks: {},

      initStack: (stackId, initialRoute, params = {}) => {
        set(state => {
          // Only initialize if stack doesn't exist or is empty
          if (!state.stacks[stackId] || state.stacks[stackId].stack.length === 0) {
            const key = generateStackKey();
            state.stacks[stackId] = {
              stack: [{ key, name: initialRoute, params }],
              currentRoute: initialRoute,
              params,
              canPop: false,
            };
          }
        });
      },

      push: (stackId, name, params = {}) => {
        set(state => {
          const stackState = state.stacks[stackId];
          if (!stackState) return;

          const key = generateStackKey();
          stackState.stack.push({ key, name, params, transitionType: "push" });
          stackState.currentRoute = name;
          stackState.params = params;
          stackState.canPop = stackState.stack.length > 1;
        });
      },

      pop: stackId => {
        set(state => {
          const stackState = state.stacks[stackId];
          if (!stackState || stackState.stack.length <= 1) return;

          stackState.stack.pop();
          const active = stackState.stack[stackState.stack.length - 1];
          stackState.currentRoute = active.name;
          stackState.params = active.params;
          stackState.canPop = stackState.stack.length > 1;
        });
      },

      replace: (stackId, name, params = {}) => {
        set(state => {
          const stackState = state.stacks[stackId];
          if (!stackState || stackState.stack.length === 0) return;

          const key = generateStackKey();
          stackState.stack[stackState.stack.length - 1] = {
            key,
            name,
            params,
            transitionType: "replace",
          };
          stackState.currentRoute = name;
          stackState.params = params;
        });
      },

      reset: (stackId, name, params = {}) => {
        set(state => {
          const key = generateStackKey();
          state.stacks[stackId] = {
            stack: [{ key, name, params }],
            currentRoute: name,
            params,
            canPop: false,
          };
        });
      },

      popTo: (stackId, targetName) => {
        set(state => {
          const stackState = state.stacks[stackId];
          if (!stackState) return;

          const targetIndex = stackState.stack.findIndex(item => item.name === targetName);
          if (targetIndex === -1) return;

          stackState.stack.splice(targetIndex + 1);
          const active = stackState.stack[stackState.stack.length - 1];
          stackState.currentRoute = active.name;
          stackState.params = active.params;
          stackState.canPop = stackState.stack.length > 1;
        });
      },
    }))
  );

// ─────────────────────────────────────────────────────────────────────────────
// Context & Provider
// ─────────────────────────────────────────────────────────────────────────────

export type StackStore = ReturnType<typeof createStackStore>;

/**
 * Context for the stack store instance.
 * @see https://docs.pmnd.rs/zustand/guides/initialize-state-with-props#creating-a-context-with-react.createcontext
 */
export const StackStoreContext = createContext<StackStore | null>(null);

/**
 * Get the stack store instance from context.
 * @throws Error if used outside of StackStoreProvider
 */
export function useStackStoreInstance(): StackStore {
  const store = useContext(StackStoreContext);
  if (!store) {
    throw new Error("useStackStoreInstance must be used within a StackStoreProvider");
  }
  return store;
}

/**
 * Hook to select state from the stack store.
 * Use narrow selectors to minimize re-renders.
 *
 * @example
 * const stacks = useStackStore(state => state.stacks);
 * const push = useStackStore(state => state.push);
 */
export function useStackStore<T>(selector: (state: StackStoreState) => T): T {
  const store = useStackStoreInstance();
  return useStore(store, selector);
}

/**
 * Hook to select state for a specific stack by ID.
 * Returns the stack state or empty state if not initialized.
 *
 * @example
 * const { stack, currentRoute, canPop } = useStackById("vehicles");
 */
export function useStackById(stackId: string): StackState {
  return useStackStore(state => state.stacks[stackId] ?? EMPTY_STACK_STATE);
}

// ─────────────────────────────────────────────────────────────────────────────
// Typed Navigation Hook (for external control)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create type-safe navigation actions for a specific stack.
 * Use this hook when you need to control a stack from outside its context.
 *
 * @example
 * const itemScreens = {
 *   "item-list": { component: ItemList },
 *   "item-detail": { component: ItemDetail }, // ItemDetail has props: { itemId: string }
 * } as const satisfies ScreensConfig;
 *
 * function ExternalController() {
 *   const nav = useTypedNavigation<typeof itemScreens>("my-stack");
 *   nav.push("item-detail", { itemId: "123" }); // ✅ typed!
 *   nav.push("item-detail"); // ❌ error: missing itemId
 *   nav.push("invalid-route"); // ❌ error: invalid route
 * }
 */
export function useTypedNavigation<T extends ScreensConfig>(
  stackId: string
): TypedNavigationActions<T> {
  const store = useStackStoreInstance();
  const canPop = useStackStore(state => state.stacks[stackId]?.canPop ?? false);

  // These are stable references from the store
  const actions = useStore(store, state => ({
    push: state.push,
    pop: state.pop,
    replace: state.replace,
    reset: state.reset,
    popTo: state.popTo,
  }));

  // Return typed wrappers that bind the stackId
  return {
    push: <K extends RoutesOf<T>>(route: K, params?: ParamsOf<T, K>) => {
      actions.push(stackId, route, params as Record<string, unknown>);
    },
    pop: () => {
      actions.pop(stackId);
    },
    replace: <K extends RoutesOf<T>>(route: K, params?: ParamsOf<T, K>) => {
      actions.replace(stackId, route, params as Record<string, unknown>);
    },
    reset: <K extends RoutesOf<T>>(route: K, params?: ParamsOf<T, K>) => {
      actions.reset(stackId, route, params as Record<string, unknown>);
    },
    popTo: <K extends RoutesOf<T>>(route: K) => {
      actions.popTo(stackId, route);
    },
    canPop,
  } as TypedNavigationActions<T>;
}

/**
 * Provider component for the stack store.
 * Wrap your app or a section of your app with this provider to enable stack navigation.
 *
 * @example
 * // In your app root or layout
 * <StackStoreProvider>
 *   <StackNavigator stackId="main" screens={screens} initialRouteName="home" />
 * </StackStoreProvider>
 *
 * @see https://docs.pmnd.rs/zustand/guides/initialize-state-with-props#wrapping-the-context-provider
 */
export function StackStoreProvider({ children }: PropsWithChildren) {
  const storeRef = useRef<StackStore | undefined>(undefined);

  if (!storeRef.current) {
    storeRef.current = createStackStore();
  }

  return (
    <StackStoreContext.Provider value={storeRef.current}>{children}</StackStoreContext.Provider>
  );
}
